import {Button} from '@/components/ui/button/Button'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {
    AlertTriangleIcon,
    CalendarIcon,
    DotsHorizontalIcon,
    File02Icon,
    LayersThree01Icon,
    Trash01Icon
} from '@/components/ui/icon/Icon'
import {Menu, MenuGroup, MenuItem} from '@/components/ui/menu-atoms/MenuAtoms'
import {useTranslation} from '@/translations/i18n'
import {MenuButton, MenuProvider} from '@ariakit/react'
import {PatientExtended} from '@/features/patients/types'
import {createSearchParams, useNavigate} from 'react-router-dom'
import {routes} from '@/utilities/constants/routes'
import {useMemo, useState} from 'react'
import {PatientDetailsOptionsCta} from '@/features/patients/components/patient-details-options-cta/PatientDetailsOptionsCta'
import {DeletePatientModal} from '@/features/patients/components/delete-patient-modal/DeletePatientModal'
import {DeceasedPatientModal} from '@/features/patients/components/deceased-patient-modal/DeceasedPatientModal'
import {CreateEventFormDialog} from '@/features/agenda/components/create-event-form-dialog/CreateEventFormDialog'
import {CreateEventFormValidationSchema} from '@/features/agenda/formSchemas'
import {usePermissions} from '@/features/auth/store/store'

export const PatientDetailsOptions = ({patient}: {patient: PatientExtended}) => {
    const {t} = useTranslation()
    const permissions = usePermissions()
    const navigate = useNavigate()
    const [isDeletePatientModalOpen, setIsDeletePatientModalOpen] = useState(false)
    const [isDeceasedPatientModalOpen, setIsDeceasedPatientModalOpen] = useState(false)

    const defaultValues = useMemo(
        (): Partial<CreateEventFormValidationSchema> => ({
            type: 'event',
            patients: [
                {
                    id: patient.id,
                    fullName: patient.fullName,
                    treatmentRequestId: patient.treatmentRequest?.id ?? null,
                    neuromuscularDiagnosisId: patient.neuromuscularDiagnosisId,
                    project: patient.project
                        ? {
                              id: patient.project.id,
                              code: patient.project.code,
                              status: patient.project.status,
                              startDate: patient.project.startDate,
                              expirationDate: patient.project.expirationDate,
                              type: patient.project.type,
                              treatmentRequestId: patient.project.treatmentRequestId
                          }
                        : null
                }
            ],
            consultants: [
                {
                    id: '',
                    fullName: ''
                }
            ],
            category: 'treatments',
            status: 'provisional'
        }),
        [patient]
    )

    if (
        !permissions?.agenda?.write &&
        !permissions?.requests?.read &&
        !permissions?.projects?.read &&
        !permissions?.patients?.write
    ) {
        return null
    }

    return (
        <>
            <Flexbox gap={2}>
                <PatientDetailsOptionsCta patient={patient} />
                <MenuProvider>
                    <MenuButton
                        render={
                            <Button variant="secondary" shape="square">
                                <DotsHorizontalIcon />
                            </Button>
                        }
                    />
                    <Menu>
                        <MenuGroup>
                            {permissions?.requests?.read && (
                                <MenuItem
                                    onClick={() =>
                                        navigate({
                                            pathname: routes.TREATMENT_REQUESTS_ROUTE.path,
                                            search: `?${createSearchParams({
                                                search: patient.fullName
                                            })}`
                                        })
                                    }
                                >
                                    <File02Icon />
                                    {t('patients:all_requests')}
                                </MenuItem>
                            )}
                            {permissions?.projects?.read && (
                                <MenuItem
                                    onClick={() =>
                                        navigate({
                                            pathname: routes.PROJECTS_ROUTE.path,
                                            search: `?${createSearchParams({
                                                search: patient.fullName
                                            })}`
                                        })
                                    }
                                >
                                    <LayersThree01Icon />
                                    {t('patients:all_projects')}
                                </MenuItem>
                            )}
                            {permissions?.agenda?.write && (
                                <CreateEventFormDialog
                                    customTrigger={({onClick}) => (
                                        <MenuItem
                                            onClick={e => {
                                                e.preventDefault()
                                                onClick(e)
                                            }}
                                        >
                                            <CalendarIcon />
                                            {t('commons:dropdown_add_event')}
                                        </MenuItem>
                                    )}
                                    defaultValues={defaultValues}
                                />
                            )}
                        </MenuGroup>
                        {permissions?.patients?.write && (
                            <>
                                <MenuGroup>
                                    <MenuItem
                                        disabled={patient.status == 'deceased'}
                                        onClick={() => setIsDeceasedPatientModalOpen(true)}
                                    >
                                        <AlertTriangleIcon />
                                        {t('commons:patient_deceased')}
                                    </MenuItem>
                                </MenuGroup>
                                <MenuGroup>
                                    <MenuItem variant="danger" onClick={() => setIsDeletePatientModalOpen(true)}>
                                        <Trash01Icon />
                                        {t('commons:delete')}
                                    </MenuItem>
                                </MenuGroup>
                            </>
                        )}
                    </Menu>
                </MenuProvider>
            </Flexbox>
            {isDeceasedPatientModalOpen && (
                <DeceasedPatientModal
                    patientId={patient.id}
                    patientFullName={patient.fullName}
                    onClose={() => setIsDeceasedPatientModalOpen(false)}
                />
            )}
            {isDeletePatientModalOpen && (
                <DeletePatientModal
                    patientId={patient.id}
                    patientFullName={patient.fullName}
                    canBeDeleted={patient.canBeDeleted}
                    onClose={() => setIsDeletePatientModalOpen(false)}
                    onSuccess={() => navigate(routes.PATIENTS_ROUTE.path, {replace: true})}
                />
            )}
        </>
    )
}
